import React from 'react';
import { useAuth } from './auth';
import { useChampionships } from './championships';

export const ROLE = {
  administrator: 5,
  head: 6,
  root: 7,
  operator: 8,
  journalist: 9,
  photographer: 10,
  doctor: 11,
  inspector: 12,
  referee: 13,
};

export const FULL_ACCESS_ROLES = [ROLE.root, ROLE.head, ROLE.administrator];
export const ADMIN_READ_ROLES = [
  ROLE.administrator,
  ROLE.head,
  ROLE.root,
  ROLE.operator,
  ROLE.journalist,
  ROLE.photographer,
  ROLE.doctor,
  ROLE.inspector,
  ROLE.referee,
];

const FALLBACK_WRITE_ROLES = {
  stadiums: [...FULL_ACCESS_ROLES, ROLE.inspector, ROLE.referee],
  matches: [...FULL_ACCESS_ROLES],
  teams: [...FULL_ACCESS_ROLES],
  players: [...FULL_ACCESS_ROLES],
  leagues: [...FULL_ACCESS_ROLES],
  tournaments: [...FULL_ACCESS_ROLES],
  disqualifications: [...FULL_ACCESS_ROLES, ROLE.referee],
  users: [...FULL_ACCESS_ROLES],
  news: [...FULL_ACCESS_ROLES, ROLE.journalist],
  reports: [],
  infographics: [...ADMIN_READ_ROLES],
  client_forms: [...FULL_ACCESS_ROLES],
};

/** Как MATCH_MEDIA_WRITE_ROLES на сервере: альбом ВК + видео без полного matches.write. */
const FALLBACK_MATCH_MEDIA_WRITE_ROLES = [
  ROLE.operator,
  ROLE.journalist,
  ROLE.photographer,
];

const FALLBACK_READ_ROLES = {
  users: [...FULL_ACCESS_ROLES],
  infographics: [...ADMIN_READ_ROLES],
  client_forms: [...FULL_ACCESS_ROLES],
};

const UNRESTRICTED_READ_EXTRA = {
  disqualifications: [ROLE.referee],
  news: [ROLE.journalist],
};

const PATH_ENTITIES = [
  { test: (p) => p.startsWith('/league_disqualifications'), entity: 'disqualifications' },
  { test: (p) => p.startsWith('/stadiums_management'), entity: 'stadiums' },
  { test: (p) => p.startsWith('/tournaments') || p.startsWith('/black_list'), entity: 'tournaments' },
  { test: (p) => p.startsWith('/teams_management'), entity: 'teams' },
  { test: (p) => p.startsWith('/players_management') || p.startsWith('/player_duplicates') || p.startsWith('/player/'), entity: 'players' },
  { test: (p) => p === '/admin' || p === '/tablo' || p.startsWith('/manage_match/') || p.startsWith('/match_staff') || p.startsWith('/select_match_media') || p.startsWith('/manage_match_media') || p.startsWith('/time_slots'), entity: 'matches' },
  { test: (p) => p.startsWith('/applications_managment') || p.startsWith('/manage_application') || p.startsWith('/team_rosters'), entity: 'teams' },
  { test: (p) => p.startsWith('/stages_management') || p.startsWith('/league_champions') || p.startsWith('/league_tour_team') || p.startsWith('/schedule_management') || p.startsWith('/transfer_windows'), entity: 'leagues' },
  { test: (p) => p.startsWith('/users'), entity: 'users' },
  { test: (p) => p.startsWith('/news'), entity: 'news' },
  { test: (p) => p.startsWith('/reports') || p.startsWith('/player_stats'), entity: 'reports' },
  { test: (p) => p.startsWith('/infographics'), entity: 'infographics' },
  { test: (p) => p.startsWith('/client_forms'), entity: 'client_forms' },
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const EMPTY_ACCESS = { read: false, write: false, unrestrictedRead: false, mediaWrite: false };
const GLOBAL_ENTITIES = new Set(['users', 'news']);

const parseRoleId = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    return parseRoleId(value.role ?? value.id ?? value.role_id ?? value.level);
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const unwrapUser = (userData) => {
  if (!userData || typeof userData !== 'object') return {};
  if (
    userData.admin_access
    || userData.champ_access
    || userData.roles
    || userData.champs_ids
    || userData.unrestricted_champs != null
    || userData.role != null
  ) {
    return userData;
  }
  if (userData.data && typeof userData.data === 'object') {
    return userData.data;
  }
  return userData;
};

export const getUserRoleAssignments = (userData) => {
  const user = unwrapUser(userData);
  const list = Array.isArray(user.roles) ? user.roles : [];
  return list.map((item) => {
    if (item && typeof item === 'object') {
      const role = parseRoleId(item);
      if (!role) return null;
      const championship_id = toNumber(
        item.championship_id ?? item.championat_id ?? item.champ_id
      );
      return {
        role,
        championship_id: championship_id && championship_id > 0 ? championship_id : null,
        championship_name: item.championship_name || item.championat_name || null,
      };
    }
    const role = parseRoleId(item);
    return role ? { role, championship_id: null, championship_name: null } : null;
  }).filter(Boolean);
};

export const getUserRoles = (userData) => {
  const assignments = getUserRoleAssignments(userData);
  if (assignments.length) {
    return [...new Set(assignments.map((item) => item.role))];
  }
  const single = parseRoleId(unwrapUser(userData).role);
  return single ? [single] : [];
};

export const hasRole = (userData, roleId) => getUserRoles(userData).includes(Number(roleId));

export const hasAnyRole = (userData, roleIds = []) => {
  const roles = getUserRoles(userData);
  return roleIds.some((roleId) => roles.includes(Number(roleId)));
};

export const hasFullAdminAccess = (userData) => hasAnyRole(userData, FULL_ACCESS_ROLES);

export const getChampsIds = (userData) => {
  const user = unwrapUser(userData);
  const raw = user.champs_ids || user.champ_ids || [];
  const fromField = Array.isArray(raw)
    ? raw.map(toNumber).filter((id) => id != null && id > 0)
    : [];
  if (fromField.length) return [...new Set(fromField)];
  return [...new Set(
    getUserRoleAssignments(user)
      .map((item) => item.championship_id)
      .filter((id) => id != null && id > 0)
  )];
};

export const hasUnrestrictedChamps = (userData) => {
  const user = unwrapUser(userData);
  if (user.unrestricted_champs === true || user.unrestricted_champs === 1) return true;
  if (user.unrestricted_champs === false || user.unrestricted_champs === 0) return false;
  if (hasRole(userData, ROLE.root)) return true;
  return hasAnyRole(userData, [ROLE.administrator, ROLE.head]) && getChampsIds(userData).length === 0;
};

export const getChampAccessList = (userData) => {
  const user = unwrapUser(userData);
  return Array.isArray(user.champ_access) ? user.champ_access : [];
};

export const getChampAccessEntry = (userData, champId) => {
  const id = toNumber(champId);
  if (!id) return null;
  return getChampAccessList(userData).find((item) => (
    toNumber(item?.championship_id ?? item?.championat_id ?? item?.id) === id
  )) || null;
};

export const getChampRoles = (userData, champId) => {
  const entry = getChampAccessEntry(userData, champId);
  if (entry && Array.isArray(entry.roles) && entry.roles.length) {
    return [...new Set(entry.roles.map(parseRoleId).filter(Boolean))];
  }
  const id = toNumber(champId);
  return getUserRoleAssignments(userData)
    .filter((item) => item.championship_id === id)
    .map((item) => item.role);
};

const readAccessMatrix = (matrix) => {
  if (!matrix || typeof matrix !== 'object') return null;
  const assignable = Array.isArray(matrix.assignable_roles)
    ? [...new Set(matrix.assignable_roles.map(toNumber).filter((id) => id != null && id > 0))]
    : null;
  return {
    read: Boolean(matrix.read),
    write: Boolean(matrix.write),
    unrestrictedRead: Boolean(matrix.unrestrictedRead),
    mediaWrite: Boolean(matrix.mediaWrite),
    ...(assignable ? { assignable_roles: assignable } : {}),
  };
};

export const getEntityAccess = (userData, entity) => {
  const user = unwrapUser(userData);
  const fromLogin = readAccessMatrix(
    user.admin_access && typeof user.admin_access === 'object'
      ? user.admin_access[entity]
      : null
  );
  if (fromLogin) return fromLogin;

  const roles = getUserRoles(userData);
  const read = roles.some((role) => (FALLBACK_READ_ROLES[entity] || ADMIN_READ_ROLES).includes(role));
  const write = roles.some((role) => (FALLBACK_WRITE_ROLES[entity] || FULL_ACCESS_ROLES).includes(role));
  const unrestrictedRead = hasUnrestrictedChamps(userData)
    || roles.some((role) => (UNRESTRICTED_READ_EXTRA[entity] || []).includes(role));
  const mediaWrite = entity === 'matches'
    ? (write || roles.some((role) => FALLBACK_MATCH_MEDIA_WRITE_ROLES.includes(role)))
    : false;
  return { read, write, unrestrictedRead, mediaWrite };
};

/** Роли, которые текущий админ может назначать (из login.admin_access.users). */
export const getAssignableRoleIds = (userData) => {
  const access = getEntityAccess(userData, 'users');
  if (Array.isArray(access.assignable_roles) && access.assignable_roles.length) {
    return access.assignable_roles;
  }
  const roles = getUserRoles(userData);
  if (roles.includes(ROLE.root)) {
    return [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
  }
  if (roles.includes(ROLE.head)) {
    return [3, 4, 5, 6, 8, 9, 10, 11, 12, 13];
  }
  if (roles.includes(ROLE.administrator)) {
    return [3, 4, 5, 8, 9, 10, 11, 12, 13];
  }
  return [];
};

export const getChampEntityAccess = (userData, entity, champId) => {
  if (!entity) return EMPTY_ACCESS;
  if (GLOBAL_ENTITIES.has(entity)) return getEntityAccess(userData, entity);

  const entry = getChampAccessEntry(userData, champId);
  const fromChamp = readAccessMatrix(entry?.access ? entry.access[entity] : null);
  if (fromChamp) return fromChamp;

  if (hasUnrestrictedChamps(userData)) return getEntityAccess(userData, entity);
  return EMPTY_ACCESS;
};

export const canReadEntity = (userData, entity) => {
  if (!entity) return Boolean(userData);
  return getEntityAccess(userData, entity).read;
};

export const canWriteEntity = (userData, entity) => {
  if (!entity) return hasUnrestrictedChamps(userData);
  return getEntityAccess(userData, entity).write;
};

export const hasUnrestrictedRead = (userData, entity) => (
  getEntityAccess(userData, entity).unrestrictedRead
);

export const canWriteChampionship = (userData, entity, champId) => {
  if (GLOBAL_ENTITIES.has(entity) || !entity) return canWriteEntity(userData, entity);
  const parsedChamp = toNumber(champId);
  if (!parsedChamp) return false;
  return getChampEntityAccess(userData, entity, parsedChamp).write;
};

/** Альбом/видео матча: matches.write ИЛИ matches.mediaWrite (оператор/журналист/фото). */
export const canWriteMatchMedia = (userData, champId = null) => {
  const parsedChamp = toNumber(champId);
  const access = parsedChamp
    ? getChampEntityAccess(userData, 'matches', parsedChamp)
    : getEntityAccess(userData, 'matches');
  if (access.write || access.mediaWrite) return true;
  // champ_access без mediaWrite, но роль 8/9/10 на этом чемпионате — fallback
  if (parsedChamp) {
    const roles = getChampRoles(userData, parsedChamp);
    if (roles.some((role) => FALLBACK_MATCH_MEDIA_WRITE_ROLES.includes(role))) return true;
  }
  return false;
};

export const canAccessChampionship = (userData, entity, champId, { write = false } = {}) => {
  if (write) return canWriteChampionship(userData, entity, champId);
  if (GLOBAL_ENTITIES.has(entity) || !entity) return canReadEntity(userData, entity);
  if (!canReadEntity(userData, entity)) return false;
  if (hasUnrestrictedRead(userData, entity) || hasUnrestrictedChamps(userData)) return true;
  const parsedChamp = toNumber(champId);
  if (!parsedChamp) return false;
  return getChampEntityAccess(userData, entity, parsedChamp).read;
};

export const filterChampionshipOptions = (options, userData, entity, { write = false } = {}) => {
  const list = Array.isArray(options) ? options : [];
  if (write) {
    return list.filter((item) => canWriteChampionship(userData, entity, item.value ?? item.id));
  }
  if (hasUnrestrictedRead(userData, entity) || hasUnrestrictedChamps(userData)) return list;
  return list.filter((item) => canAccessChampionship(userData, entity, item.value ?? item.id));
};

export const filterChampionshipGroups = (groups, userData) => {
  const list = Array.isArray(groups) ? groups : [];
  if (hasUnrestrictedChamps(userData)) return list;
  const allowed = new Set(getChampsIds(userData));
  if (!allowed.size) return [];
  return list
    .map((city) => ({
      ...city,
      championships: (city.championships || []).filter((item) => allowed.has(toNumber(item.id))),
    }))
    .filter((city) => city.championships.length > 0);
};

export const entityFromPath = (pathname = '') => {
  const path = String(pathname || '');
  const match = PATH_ENTITIES.find((item) => item.test(path));
  return match ? match.entity : null;
};

export const isWriteOnlyPath = (pathname = '') => /(^|\/)new(\/|$)/.test(String(pathname || ''));

export const canAccessPath = (userData, pathname) => {
  const entity = entityFromPath(pathname);
  if (!canReadEntity(userData, entity)) return false;
  if (isWriteOnlyPath(pathname) && !canWriteEntity(userData, entity)) return false;
  return true;
};

const LANDING_PATHS = [
  ['matches', '/admin'],
  ['disqualifications', '/league_disqualifications'],
  ['stadiums', '/stadiums_management'],
  ['tournaments', '/tournaments'],
  ['teams', '/teams_management'],
  ['players', '/players_management'],
  ['leagues', '/schedule_management'],
  ['users', '/users'],
  ['news', '/news'],
  ['reports', '/reports'],
];

export const firstAllowedPath = (userData) => {
  const match = LANDING_PATHS.find(([entity]) => canReadEntity(userData, entity));
  return match ? match[1] : '/';
};

export function useAdminAccess(entity) {
  const { userData } = useAuth();
  const championships = useChampionships();
  const champId = championships?.champId || null;
  const access = getEntityAccess(userData, entity);
  const champAccess = getChampEntityAccess(userData, entity, champId);
  const canWrite = canWriteChampionship(userData, entity, champId);
  const canWriteMedia = entity === 'matches'
    ? canWriteMatchMedia(userData, champId)
    : canWrite;

  return {
    userData,
    champId,
    roles: getUserRoles(userData),
    champRoles: getChampRoles(userData, champId),
    champsIds: getChampsIds(userData),
    unrestrictedChamps: hasUnrestrictedChamps(userData),
    hasFullAccess: hasUnrestrictedChamps(userData),
    canRead: access.read,
    canWrite,
    canWriteMedia,
    canWriteAny: access.write,
    unrestrictedRead: access.unrestrictedRead || champAccess.unrestrictedRead,
    canReadChamp: canAccessChampionship(userData, entity, champId),
    assignableRoles: entity === 'users' ? getAssignableRoleIds(userData) : [],
  };
}

export function IfCanWrite({ entity, children }) {
  const { canWrite } = useAdminAccess(entity);
  if (!canWrite) return null;
  return <>{children}</>;
}

/** Кнопки альбома/видео: write или mediaWrite. */
export function IfCanWriteMatchMedia({ children }) {
  const { canWriteMedia } = useAdminAccess('matches');
  if (!canWriteMedia) return null;
  return <>{children}</>;
}
